import { useCallback } from "react";
import { useInfiniteQuery, type QueryKey } from "@tanstack/react-query";
import { TABLE_PAGE_SIZE } from "../../constants/tables";
import type { Page } from "../../types/api";

export function useInfinitePage<T>(
  queryKey: QueryKey,
  fetchPage: (page: number, pageSize: number) => Promise<Page<T>>,
  options?: { enabled?: boolean },
) {
  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    enabled: options?.enabled,
    queryFn: ({ pageParam }) => fetchPage(pageParam, TABLE_PAGE_SIZE),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
      if (lastPage.items.length === 0 || loaded >= lastPage.total) return undefined;
      return lastPage.page + 1;
    },
  });

  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages.at(-1)?.total ?? 0;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    items,
    total,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasMore: Boolean(query.hasNextPage),
    loadingMore: query.isFetchingNextPage,
    loadMore,
  };
}
